import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import './DashboardIntervenant.css';

// Import des icônes MUI (On garde les icônes car ce sont des composants valides)
import {
  WavingHandOutlined as WaveIcon,
  TrendingUp as TrendIcon,
  ShoppingCartOutlined as OrderIcon,
  PeopleOutlined as ClientIcon,
  VideoLibraryOutlined as VideoIcon,
  ClassOutlined as WorkshopIcon,
  ChatBubbleOutline as ChatIcon,
  ChevronRight as ViewAllIcon,
  CategoryOutlined as CategoryIcon,
} from '@mui/icons-material';

const DashboardIntervenant = () => {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const storageUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${storageUrl}/intervenant/dashboard/summary-stats`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setInfo(res.data.data);
      } catch (err) {
        console.error(err);
      }
    };
    if (user) fetchStats();
  }, [user, storageUrl]);

  if (!user) return null;

  const statsCards = [
    {
      label: 'Commandes',
      val: info?.summary?.ordersCount || 0,
      color: 'var(--primary)',
      icon: <OrderIcon />,
    },
    {
      label: 'Clients',
      val: info?.summary?.clientsCount || 0,
      color: 'var(--info)',
      icon: <ClientIcon />,
    },
    {
      label: 'Ateliers',
      val: info?.summary?.workshopsCount || 0,
      color: 'var(--warning)',
      icon: <WorkshopIcon />,
    },
    {
      label: 'Vidéos',
      val: info?.summary?.videosCount || 0,
      color: 'var(--danger)',
      icon: <VideoIcon />,
    },
    {
      label: 'Catégories',
      val: info?.summary?.categoriesCount || 0,
      color: 'var(--primary)',
      icon: <CategoryIcon />,
    },
  ];

  return (
    <div className="dashboard-wrapper">
      {/* --- TOPBAR --- */}

      <main className="dashboard-content">
        {/* --- BANNER --- */}
        <div className="dashboard-banner">
          <div className="banner-info">
            <div className="banner-title">
              <WaveIcon className="wave-icon" />
              <h1>Bienvenue, {user.prenom} !</h1>
            </div>
            <p>Nous sommes ravis de vous revoir sur votre tableau de bord.</p>
          </div>
          <TrendIcon className="banner-bg-icon" />
        </div>

        {/* --- STATS GRID --- */}
        <div className="stats-grid">
          {statsCards.map((s, i) => (
            <div
              className="stat-card"
              key={i}
              style={{ borderBottomColor: s.color }}
            >
              <div className="stat-data">
                <span className="stat-number">{s.val}</span>
                <span className="stat-label">{s.label}</span>
              </div>
              <div className="stat-icon-wrapper" style={{ color: s.color }}>
                {s.icon}
              </div>
            </div>
          ))}
        </div>

        {/* --- MAIN GRID (2 COLONNES PC / 1 COL MOBILE) --- */}
        <div className="dashboard-main-grid">
          {/* TABLEAU */}
          <div className="content-card ventes-card">
            <div className="card-header">
              <h3>Ventes Récentes</h3>
              <button className="btn-outline">
                Détails <ViewAllIcon />
              </button>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>CLIENT</th>
                    <th>PRODUIT</th>
                    <th>DATE</th> {/* Nouvelle colonne Date */}
                    <th>MONTANT</th>
                    <th>STATUT</th>
                  </tr>
                </thead>
                <tbody>
                  {info?.recentOrders?.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <div className="client-info">
                          <div className="avatar-circle">
                            {order.client_nom.charAt(0)}
                          </div>
                          <span>{order.client_nom}</span>
                        </div>
                      </td>
                      <td>
                        <div className="product-info">
                          <div className="product-label">{order.label}</div>
                          {/* Description avec classe truncate et title pour le survol */}
                          <div
                            className="product-description"
                            title={order.description}
                          >
                            {order.description}
                          </div>
                        </div>
                      </td>
                      <td className="date-cell">{order.date}</td>
                      <td className="amount">{order.total}€</td>
                      <td>
                        <span
                          className={`status-chip ${order.etat.toLowerCase()}`}
                        >
                          {order.etat}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* STAFF */}
          <div className="content-card staff-card">
            <div className="card-header">
              <h3>Staff en ligne ({info?.summary?.staffCount || 0})</h3>
            </div>
            <div className="staff-list">
              {info?.recentIntervenants?.map((inter) => (
                <div className="staff-item" key={inter.id}>
                  <div className="avatar-badge">
                    <img
                      src={`${storageUrl}/storage/workshop/${inter.photo}`}
                      alt="staff"
                    />
                    <span
                      className={`status-dot ${inter.is_connected ? 'online' : ''}`}
                    ></span>
                  </div>
                  <div className="staff-details">
                    <p className="staff-name">{inter.nom}</p>
                    <p className="staff-role">{inter.role || 'Intervenant'}</p>
                  </div>
                  <button className="chat-btn">
                    <ChatIcon />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* --- NOUVELLE SECTION : OFFRES ET ATELIERS --- */}
        <div className="dashboard-secondary-grid">
          {/* OFFRES RÉCENTES */}
          <div className="content-card offers-card">
            <div className="card-header">
              <h3>Offres Récentes</h3>
              <button className="btn-outline">Voir tout</button>
            </div>
            <div className="offers-list">
              {info?.recentOffers?.map((offer) => (
                <div className="offer-item" key={offer.id}>
                  <img
                    src={`${storageUrl}/storage/workshop/${offer.intervenant_image}`}
                    className="offer-intervenant-img"
                    alt={offer.intervenant_nom}
                  />
                  <div className="offer-details">
                    <p className="offer-title">{offer.titre}</p>
                    <p className="offer-subtitle">
                      {offer.intervenant_nom} • {offer.type.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="offer-price-tag">{offer.prix}€</div>
                </div>
              ))}
            </div>
          </div>

          {/* ATELIERS RÉCENTS */}
          <div className="content-card workshops-card">
            <div className="card-header">
              <h3>Ateliers Récents</h3>
            </div>
            <div className="workshops-list">
              {info?.recentWorkshops?.map((ws) => (
                <div className="workshop-item" key={ws.id}>
                  <div className="workshop-icon-box">
                    <WorkshopIcon />
                  </div>
                  <div className="workshop-info">
                    <div className="workshop-top">
                      <p className="workshop-name">{ws.titre}</p>
                      <span className="workshop-duration">{ws.duree} min</span>
                    </div>
                    <p className="workshop-desc">
                      {(ws.description || '').substring(0, 60)}
                      {ws.description ? '...' : ''}
                    </p>{' '}
                    <div className="workshop-footer">
                      <span className="workshop-price">{ws.prix}€</span>
                      <span className="workshop-cap">
                        Capacité: {ws.capacite} pers.
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardIntervenant;
